import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck, ArrowRight, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { Logo } from "@/components/app/Logo";

const Setup = () => {
  const nav = useNavigate();
  const [check, setCheck] = useState({ loading: true, needs: true });
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/setup/status")
      .then(({ data }) => setCheck({ loading: false, needs: !!data.needs_setup }))
      .catch(() => setCheck({ loading: false, needs: false }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Senha deve ter ao menos 6 caracteres");
    setBusy(true);
    try {
      const { data } = await api.post("/setup/admin", form);
      localStorage.setItem("fidelis_token", data.access_token);
      localStorage.setItem("fidelis_user", JSON.stringify(data.user));
      toast.success("Administrador criado com sucesso!");
      setTimeout(() => { window.location.href = "/admin"; }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar administrador");
    } finally { setBusy(false); }
  };

  if (check.loading) return <div className="min-h-screen grid place-items-center text-zinc-500">Verificando...</div>;
  if (!check.needs) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-zinc-50 to-white px-4 py-10">
      <Toaster position="top-right" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <Logo variant="full" height={56} />
        </div>

        <Card className="border-zinc-200 p-8 card-soft">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"/>
            <p className="text-xs tracking-[0.3em] uppercase text-emerald-700 font-semibold">Configuração inicial</p>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Crie o primeiro administrador</h1>
          <p className="text-sm text-zinc-600 mt-2">
            Esta tela aparece apenas uma vez. Após criar, você acessará o painel diretamente.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Seu nome</Label>
              <Input
                className="mt-1.5 h-11"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex: Maria Silva"
                required
                data-testid="setup-name"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Usuário (login)</Label>
              <Input
                className="mt-1.5 h-11 lowercase"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "").toLowerCase() })}
                placeholder="ex: maria"
                required
                data-testid="setup-username"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">E-mail</Label>
              <Input
                className="mt-1.5 h-11"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="seu@email.com"
                required
                data-testid="setup-email"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Senha (mín. 6)</Label>
              <div className="relative mt-1.5">
                <Input
                  className="h-11 pr-10"
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="setup-password"
                />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800">
                  {showPwd ? <EyeSlash size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
              <ShieldCheck className="text-blue-600 mt-0.5 flex-shrink-0" size={16} weight="duotone"/>
              <span>Suas credenciais ficam armazenadas com criptografia bcrypt. Você poderá alterar depois nas configurações.</span>
            </div>

            <Button type="submit" disabled={busy} data-testid="setup-submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? "Criando..." : (<>Criar administrador <ArrowRight className="ml-2" size={16} weight="bold"/></>)}
            </Button>
          </form>
        </Card>

        <div className="text-center text-xs text-zinc-500 mt-6 flex items-center justify-center gap-1.5">
          <CheckCircle weight="fill" className="text-emerald-500" size={14}/>
          Sistema instalado · pronto para configurar
        </div>
      </div>
    </div>
  );
};

export default Setup;
